import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, NumberInput, Select, SelectItem, Tag, TextInput } from '@carbon/react';
import { ArrowLeft, TrashCan } from '@carbon/icons-react';
import { formatVnDateTime } from '@ruby-cms/shared';
import { api } from '../api/client';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { CURRENCY, When } from '../components/format';
import { HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';
import { ChonKetQua } from './forced/Pickers';
import type { Cuoc, DuDoan, Game, KQ, Lenh, LichSu, Man, ManData, Van } from './forced/types';
import { GIAI_DOAN, TRANG_THAI, TRANG_THAI_TAG, bangNhau, maNgan, moTaKQ, tenCua, vnd } from './forced/types';
import './forced/forced.scss';

// MAN DAT KET QUA RIENG CUA MOT GAME (quyet dinh 18, chi founder).
// Game van chung: van dang chay (phien, giai doan, dem nguoc), cuoc NGUOI THAT tung cua (khong
// bot), chot ket qua cho chinh van nay, lai/lo nha cai theo tung ket qua, cau (lich su + chuoi
// van toi), va moi lenh cua game. Game theo nguoi choi: ai dang choi, luot gan day, dat N luot toi.

type Gui = { hanh_dong: 'tao' | 'cau' | 'huy' | 'huy_het' } & Record<string, unknown>;

function useDemNguoc(conLaiMs: number | undefined, luc: number) {
  const [bay, setBay] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setBay(Date.now()), 250);
    return () => clearInterval(t);
  }, []);
  return conLaiMs == null ? null : Math.max(0, conLaiMs - (bay - luc));
}
const dongHo = (ms: number) => {
  const s = Math.ceil(ms / 1000);
  return String(Math.floor(s / 60)).padStart(2, '0') + ':' + String(s % 60).padStart(2, '0');
};
const tienCuoc = (c: Cuoc) => <span className="num">{vnd(c.tien)}{c.loai === 'xu' ? <span className="muted"> xu</span> : null}</span>;
const lucCuoc = (l: Cuoc['luc']) => {
  if (l == null) return '';
  const d = new Date(typeof l === 'string' && /^\d{4}-\d{2}-\d{2} /.test(l) ? l.replace(' ', 'T') + 'Z' : l);
  return Number.isNaN(d.getTime()) ? '' : formatVnDateTime(d).slice(11);
};
const NguoiChoiLink = ({ id, nick }: { id: number | string; nick: string | null }) => (
  <Link className="cell-link" to={'/users/' + id}>{nick || 'Người chơi'} <span className="muted num">#{id}</span></Link>
);
function Lai({ v }: { v: number }) {
  return <span className={'num' + (v > 0 ? ' num--pos' : v < 0 ? ' num--neg' : '')}>{v > 0 ? '+' : ''}{vnd(v)}</span>;
}

export function ForcedGamePage() {
  const { gid = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['ep-ket-qua', gid],
    queryFn: () => api.get<ManData>('/api/admin/games/ep-ket-qua/' + encodeURIComponent(gid)),
    refetchInterval: (s) => (s.state.data?.game.loai === 'nguoi' ? 3000 : 1000),
  });
  const [xong, setXong] = useState<Command | null>(null);
  const gui = useMutation({
    mutationFn: (body: Gui) => api.post<{ command: Command }>('/api/admin/games/ep-ket-qua', body),
    onSuccess: (r) => { setXong(r.command); qc.invalidateQueries({ queryKey: ['ep-ket-qua'] }); },
  });

  if (q.isLoading) return <BlockSkeleton lines={8} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  const g = d.game;

  return (
    <div className="page fr">
      <PageHeader title={'Đặt kết quả: ' + g.ten}
        lead={g.loai === 'van'
          ? 'Chỉ chủ sở hữu. Xem cược thật của ván đang chạy, chốt kết quả cho chính ván này, hoặc xếp cầu cho các ván tới. Lệnh không sửa ván đã xong.'
          : 'Chỉ chủ sở hữu. Đặt kết quả cho các lượt tới của một người chơi. Lệnh không sửa lượt đã xong.'}
        actions={<Link to="/games/force"><Button kind="ghost" size="sm" renderIcon={ArrowLeft}>Tất cả game</Button></Link>} />
      {g.ghiChu ? <InlineNotification kind="info" lowContrast hideCloseButton title="Lưu ý" subtitle={g.ghiChu} /> : null}
      {xong ? <KetQuaLenh cmd={xong} onClose={() => setXong(null)} /> : null}
      {gui.error ? <InlineError error={gui.error} /> : null}
      {g.loai === 'van'
        ? <ManVan d={d} luc={q.dataUpdatedAt} gui={gui.mutate} dangGui={gui.isPending} />
        : <ManNguoi d={d} gui={gui.mutate} dangGui={gui.isPending} />}
      <LenhPanel g={g} lenh={d.lenh} gui={gui.mutate} dangGui={gui.isPending} />
    </div>
  );
}

function KetQuaLenh({ cmd, onClose }: { cmd: Command; onClose: () => void }) {
  const ok = cmd.status === 'SUCCEEDED';
  const loi = cmd.lastError ? (HE_A_ERRORS[cmd.lastError] ?? cmd.lastError) + '. ' : '';
  return (
    <div>
      <InlineNotification kind={ok ? 'success' : 'warning'} lowContrast onClose={() => { onClose(); return false; }}
        title={ok ? 'Hệ A đã nhận lệnh' : 'Lệnh chưa xong'} subtitle={loi || 'Lệnh đã ghi, áp cho ván hoặc lượt tương ứng.'} />
      <p><Link className="cell-link" to={'/commands/' + cmd.id}>Xem lệnh</Link></p>
    </div>
  );
}

// ================================================================ game van chung

function ManVan({ d, luc, gui, dangGui }: { d: ManData; luc: number; gui: (b: Gui) => void; dangGui: boolean }) {
  const g = d.game, van = d.van, man = g.man;
  const [chon, setChon] = useState<KQ | null>(null);
  const [khoaChon, setKhoaChon] = useState(0);   // doi key -> bo chon ve trang
  // Phien chot giu tu luc mo hop xac nhan: van troi sang phien moi thi khong gui sang phien khac
  const [xacNhan, setXacNhan] = useState<{ phien: string; ten: string } | null>(null);
  const dem = useDemNguoc(van?.con_lai_ms, luc);
  const phienLenh = van ? (van.phien_lenh ?? van.phien) : null;
  // Ten hien cho nguoi doc: Keno / lo de dung ma ky hien (phien_hien), khong phai ma noi bo
  const tu = man === 'keno' || man === 'lode' ? 'kỳ' : 'phiên';
  const tenPhien = van && phienLenh === van.phien && van.phien_hien ? van.phien_hien : phienLenh;
  const daQua = !!xacNhan && (!van?.nhan_lenh || phienLenh !== xacNhan.phien);
  const lenhVanNay = d.lenh.find((l) => l.phien != null && l.phien === phienLenh && (l.trang_thai === 'CHO' || l.trang_thai === 'DA_DUNG'));
  const duDoanChon = van?.du_doan.find((x) => bangNhau(x.ket_qua, chon));

  return (
    <>
      <section className="fr-live panel">
        {van ? (
          <>
            <div className="fr-live__main">
              <div>
                <span className="fr-label">{man === 'keno' || man === 'lode' ? 'Kỳ đang mở' : 'Phiên đang chạy'}</span>
                <div className="fr-live__phien num">#{van.phien_hien ?? van.phien}</div>
                {van.ten_kenh ? <span className="muted">{van.ten_kenh}</span> : null}
              </div>
              <div>
                <span className="fr-label">Giai đoạn</span>
                <div><Tag type={van.giai_doan === 'cuoc' ? 'green' : 'cool-gray'}>{GIAI_DOAN[van.giai_doan] ?? van.giai_doan}</Tag></div>
              </div>
              <div>
                <span className="fr-label">{van.giai_doan === 'cuoc' ? 'Còn lại' : 'Tới mốc tiếp theo'}</span>
                <div className={'fr-live__dem num' + (dem !== null && dem < 5000 && van.giai_doan === 'cuoc' ? ' is-gap' : '')}>{dem !== null ? dongHo(dem) : '-'}</div>
              </div>
              {van.quy ? (
                <div>
                  <span className="fr-label">Quỹ nhà cái</span>
                  <div>{van.quy.bat ? <span className="num">{vnd(van.quy.so_du ?? 0)}</span> : <span className="muted">Tắt (người chơi ăn thua với nhau)</span>}</div>
                </div>
              ) : null}
              {man === 'aviator' ? (
                <div>
                  <span className="fr-label">Điểm nổ đã định cho ván này</span>
                  <div className="fr-live__phien num">{van.diem_no?.toFixed(2)}x</div>
                  {van.giai_doan === 'bay' ? <span className="muted num">đang bay {van.he_so?.toFixed(2)}x</span> : null}
                </div>
              ) : null}
            </div>
            <KetQuaHienTai man={man} van={van} />
          </>
        ) : (
          <p className="muted">Không đọc được ván đang chạy ({d.van_loi ?? 'lỗi'}). Hàng đợi và cầu vẫn đặt được.</p>
        )}
      </section>

      <div className="fr-cols">
        <CuocPanel man={man} van={van} />
        <section className="panel fr-chot">
          <div className="panel__head"><h2>{man === 'keno' || man === 'lode' ? 'Chốt kỳ này' : 'Chốt ván này'}</h2></div>
          {lenhVanNay ? (
            <InlineNotification kind={lenhVanNay.trang_thai === 'DA_DUNG' ? 'success' : 'info'} lowContrast hideCloseButton
              title={(lenhVanNay.trang_thai === 'DA_DUNG' ? 'Đã áp: ' : 'Đã đặt: ') + moTaKQ(man, lenhVanNay.ket_qua)}
              subtitle={`Lệnh #${lenhVanNay.id} cho ${tu} #${tenPhien}. Đặt lại sẽ thay lệnh này.`} />
          ) : null}
          <ChonKetQua key={khoaChon} man={man} game={g} value={chon} onChange={setChon} />
          {chon && duDoanChon ? (
            <p className="fr-readout">Nếu ra kết quả này: người chơi thật được trả <span className="num">{vnd(duDoanChon.tra)}</span>, nhà cái <Lai v={duDoanChon.lai} /></p>
          ) : null}
          <div className="row">
            <Button kind="primary" disabled={!chon || !van?.nhan_lenh || dangGui} onClick={() => phienLenh && setXacNhan({ phien: phienLenh, ten: tenPhien ?? phienLenh })}>
              {van?.nhan_lenh ? `Chốt cho ${tu} #${tenPhien}` : tu === 'kỳ' ? 'Kỳ này đã chốt kết quả' : 'Ván này đã chốt kết quả'}
            </Button>
          </div>
          {van && !van.nhan_lenh ? <p className="muted fr-note">Chờ ván mới mở rồi chốt, hoặc xếp vào cầu bên dưới.</p> : null}
          <CauBuilder man={man} g={g} chon={chon} gui={gui} dangGui={dangGui} />
        </section>
      </div>

      {van && van.du_doan.length ? <DuDoanPanel man={man} van={van} chon={chon} onChon={(k) => setChon(k)} /> : null}
      <CauBang man={man} van={van} lenh={d.lenh} />

      <Modal open={!!xacNhan} modalHeading={`Chốt kết quả ${tu} #${xacNhan?.ten ?? ''}`} primaryButtonText="Gửi sang hệ A" secondaryButtonText="Hủy"
        primaryButtonDisabled={dangGui || daQua} onRequestClose={() => setXacNhan(null)}
        onRequestSubmit={() => {
          if (!chon || !xacNhan || daQua) return;
          gui({ hanh_dong: 'tao', game_id: g.id, ket_qua: chon, so_lan: 1, phien: xacNhan.phien });
          setXacNhan(null); setChon(null); setKhoaChon((x) => x + 1);
        }}>
        {daQua ? <InlineNotification kind="warning" lowContrast hideCloseButton title={`${tu === 'kỳ' ? 'Kỳ' : 'Phiên'} #${xacNhan?.ten} đã qua`} subtitle="Lệnh chưa gửi. Đóng hộp này và chốt lại cho ván đang chạy." /> : null}
        <p>{g.ten}, {tu} #{xacNhan?.ten} sẽ ra: <strong>{moTaKQ(man, chon)}</strong>.</p>
        {duDoanChon ? <p>Theo cược thật lúc này: trả người chơi {vnd(duDoanChon.tra)}, nhà cái <Lai v={duDoanChon.lai} />. Cược còn vào tới lúc đóng cửa.</p> : null}
        <p className="muted">Lệnh này quyết định tiền người chơi nhận. Nếu ván chốt trước khi lệnh tới, lệnh hết hạn và không áp cho ván khác.</p>
      </Modal>
    </>
  );
}

function KetQuaHienTai({ man, van }: { man: Man; van: Van }) {
  const k = van.ket_qua;
  if (!k) return null;
  let text: string;
  if (Array.isArray(k.xuc_xac) && man === 'tai_xiu') text = moTaKQ(man, { xuc_xac: k.xuc_xac });
  else if (Array.isArray(k.cua_thang)) text = (k.cua_thang as number[]).map((c) => tenCua(man, c, van)).join(' + ') + (typeof k.mo_ta === 'string' ? ` (${k.mo_ta})` : '');
  else if (typeof k.diem_no === 'number') text = k.diem_no.toFixed(2) + 'x';
  else text = typeof k.mo_ta === 'string' ? k.mo_ta : JSON.stringify(k);
  return (
    <p className="fr-live__kq">
      <span className="fr-label">Kết quả ván này</span>
      <strong>{text}</strong>
    </p>
  );
}

// ---------------------------------------------------------------- cuoc that

function CuocPanel({ man, van }: { man: Man; van: Van | null }) {
  if (!van) return <section className="panel"><div className="panel__head"><h2>Cược thật ván này</h2></div><p className="muted">Chưa đọc được ván.</p></section>;
  const cuoc = van.cuoc;
  const tongVin = cuoc.filter((c) => c.loai === 'vin').reduce((a, c) => a + c.tien, 0);
  const soNguoi = new Set(cuoc.map((c) => String(c.user_id))).size;
  const head = (
    <div className="panel__head">
      <h2>Cược thật ván này</h2>
      <span className="muted">{soNguoi} người, {cuoc.length} lệnh, <span className="num">{CURRENCY.symbol}{vnd(tongVin)}</span>. Không tính bot.</span>
    </div>
  );
  if (man === 'tai_xiu') {
    return (
      <section className="panel">
        {head}
        <div className="fr-2ben">
          {[1, 0].map((cua) => {
            const ds = cuoc.filter((c) => Number(c.cua) === cua).slice().reverse();
            const tong = ds.filter((c) => c.loai === 'vin').reduce((a, c) => a + c.tien, 0);
            return (
              <div key={cua} className={'fr-ben ' + (cua === 1 ? 'fr-ben--tai' : 'fr-ben--xiu')}>
                <div className="fr-ben__head">
                  <strong>{cua === 1 ? 'TÀI' : 'XỈU'}</strong>
                  <span className="num fr-ben__tong">{vnd(tong)}</span>
                  <span className="muted">{new Set(ds.map((c) => String(c.user_id))).size} người, {ds.length} lệnh</span>
                </div>
                <div className="fr-scroll">
                  {ds.length ? (
                    <table className="plain fr-table">
                      <tbody>
                        {ds.map((c, i) => (
                          <tr key={i}>
                            <td><NguoiChoiLink id={c.user_id} nick={c.nick} /></td>
                            <td className="fr-r">{tienCuoc(c)}</td>
                            <td className="fr-r muted num">{lucCuoc(c.luc)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p className="muted fr-empty">Chưa có cược thật.</p>}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  }
  if (man === 'aviator') {
    return (
      <section className="panel">
        {head}
        <div className="fr-scroll fr-scroll--tall">
          {cuoc.length ? (
            <table className="plain fr-table">
              <thead><tr><th>Người chơi</th><th className="fr-r">Cược</th><th className="fr-r">Tự rút</th><th className="fr-r">Đã rút</th><th className="fr-r">Thắng</th></tr></thead>
              <tbody>
                {cuoc.slice().sort((a, b) => b.tien - a.tien).map((c, i) => (
                  <tr key={i}>
                    <td><NguoiChoiLink id={c.user_id} nick={c.nick} /></td>
                    <td className="fr-r">{tienCuoc(c)}</td>
                    <td className="fr-r num">{c.tu_rut ? c.tu_rut.toFixed(2) + 'x' : <span className="muted">tay</span>}</td>
                    <td className="fr-r num">{c.da_rut ? c.da_rut.toFixed(2) + 'x' : <span className="muted">-</span>}</td>
                    <td className="fr-r num">{c.thang ? vnd(c.thang) : <span className="muted">-</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="muted fr-empty">Chưa có cược thật.</p>}
        </div>
      </section>
    );
  }
  if (man === 'keno' || man === 'lode') {
    return (
      <section className="panel">
        {head}
        <div className="fr-scroll fr-scroll--tall">
          {cuoc.length ? (
            <table className="plain fr-table">
              <thead><tr><th>Người chơi</th><th>Loại</th><th>Số chọn</th><th className="fr-r">Tiền</th><th className="fr-r">Lúc</th></tr></thead>
              <tbody>
                {cuoc.slice().reverse().map((c, i) => (
                  <tr key={i}>
                    <td><NguoiChoiLink id={c.user_id} nick={c.nick} /></td>
                    <td>{tenCua(man, c.cua, van)}</td>
                    <td className="num fr-so">{Array.isArray(c.so) ? c.so.join(' ') : c.so || <span className="muted">-</span>}</td>
                    <td className="fr-r">{tienCuoc(c)}</td>
                    <td className="fr-r muted num">{lucCuoc(c.luc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="muted fr-empty">Chưa có vé thật trong kỳ này.</p>}
        </div>
      </section>
    );
  }
  // ban / mini game nhieu cua: tong tung cua roi tung lenh
  const theoCua = new Map<string, { tien: number; nguoi: Set<string>; lenh: number }>();
  for (const c of cuoc) {
    const k = String(c.cua);
    const x = theoCua.get(k) ?? { tien: 0, nguoi: new Set<string>(), lenh: 0 };
    if (c.loai === 'vin') x.tien += c.tien;
    x.nguoi.add(String(c.user_id)); x.lenh++;
    theoCua.set(k, x);
  }
  const cuaCo = [...theoCua.entries()].sort((a, b) => b[1].tien - a[1].tien);
  return (
    <section className="panel">
      {head}
      {cuaCo.length ? (
        <>
          <div className="fr-doors">
            {cuaCo.map(([k, x]) => (
              <div key={k} className="fr-door">
                <span className="fr-door__ten">{tenCua(man, k, van)}</span>
                <span className="num fr-door__tien">{vnd(x.tien)}</span>
                <span className="muted">{x.nguoi.size} người</span>
              </div>
            ))}
          </div>
          <div className="fr-scroll">
            <table className="plain fr-table">
              <thead><tr><th>Người chơi</th><th>Cửa</th><th className="fr-r">Tiền</th></tr></thead>
              <tbody>
                {cuoc.slice().sort((a, b) => b.tien - a.tien).map((c, i) => (
                  <tr key={i}>
                    <td><NguoiChoiLink id={c.user_id} nick={c.nick} /></td>
                    <td>{tenCua(man, c.cua, van)}</td>
                    <td className="fr-r">{tienCuoc(c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : <p className="muted fr-empty">Chưa có cược thật trong ván này.</p>}
    </section>
  );
}

// ---------------------------------------------------------------- lai / lo theo ket qua

function DuDoanPanel({ man, van, chon, onChon }: { man: Man; van: Van; chon: KQ | null; onChon: (k: KQ) => void }) {
  const [xep, setXep] = useState<'lai' | 'lo' | 'xs'>('lai');
  const ds = useMemo(() => van.du_doan.slice().sort((a, b) =>
    xep === 'lai' ? b.lai - a.lai : xep === 'lo' ? a.lai - b.lai : (b.xac_suat ?? 0) - (a.xac_suat ?? 0)), [van.du_doan, xep]);
  const coHoan = van.du_doan.some((x) => (x.hoan ?? 0) > 0);
  const moTa = (x: DuDoan) => (x.cua_thang && man !== 'sicbo' && !x.ket_qua.xuc_xac ? x.cua_thang.map((c) => tenCua(man, c, van)).join(' + ') : moTaKQ(man, x.ket_qua));
  return (
    <section className="panel">
      <div className="panel__head">
        <h2>Nhà cái lãi / lỗ theo từng kết quả</h2>
        <Select id="fr-xep" labelText="" hideLabel size="sm" inline value={xep} onChange={(e) => setXep(e.target.value as typeof xep)}>
          <SelectItem value="lai" text="Nhà cái lãi nhiều nhất trước" />
          <SelectItem value="lo" text="Nhà cái lỗ nhiều nhất trước" />
          <SelectItem value="xs" text="Hay ra nhất trước" />
        </Select>
      </div>
      <p className="muted fr-note">
        Tính trên cược thật lúc này, đúng cách hệ thống trả thưởng{man === 'tai_xiu' ? ' (kể cả hoàn cân cửa và hạn mức quỹ)' : ''}.
        {man === 'aviator' ? ' Chỉ tính cược đã rút và cược đặt tự rút; cược rút tay chưa biết trước.' : ''}
        {man === 'baccarat' || man === 'rong_ho' || man === 'xeng' ? ' Bài chia ngẫu nhiên đến khi đúng các cửa thắng đã chọn.' : ''}
      </p>
      <div className="fr-scroll fr-scroll--tall">
        <table className="plain fr-table">
          <thead>
            <tr>
              <th>Kết quả</th>
              {van.du_doan.some((x) => x.xac_suat != null) ? <th className="fr-r">Xác suất</th> : null}
              <th className="fr-r">Cược thật</th>
              {coHoan ? <th className="fr-r">Hoàn cân cửa</th> : null}
              <th className="fr-r">Trả người chơi</th>
              <th className="fr-r">Nhà cái</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ds.map((x, i) => (
              <tr key={i} className={bangNhau(x.ket_qua, chon) ? 'is-chon' : ''}>
                <td>{moTa(x)}</td>
                {van.du_doan.some((y) => y.xac_suat != null) ? <td className="fr-r num">{x.xac_suat != null ? (x.xac_suat * 100).toFixed(x.xac_suat < 0.01 ? 2 : 1) + '%' : '-'}</td> : null}
                <td className="fr-r num">{vnd(x.cuoc)}</td>
                {coHoan ? <td className="fr-r num">{vnd(x.hoan ?? 0)}</td> : null}
                <td className="fr-r num">{vnd(x.tra)}</td>
                <td className="fr-r"><Lai v={x.lai} /></td>
                <td className="fr-r"><Button size="sm" kind="ghost" onClick={() => onChon(x.ket_qua)}>Chọn</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- cau

// Xep chuoi ket qua cho N van toi. Tai Xiu co mau cau san (bet, 1-1, 2-2, 1-2).
function CauBuilder({ man, g, chon, gui, dangGui }: { man: Man; g: Game; chon: KQ | null; gui: (b: Gui) => void; dangGui: boolean }) {
  const [cau, setCau] = useState<KQ[]>([]);
  const [lap, setLap] = useState(1);
  const [soVan, setSoVan] = useState(6);
  const [xn, setXn] = useState(false);
  const them = (ds: KQ[]) => setCau((c) => [...c, ...ds].slice(0, 100));
  const T: KQ = { cua: 'tai' }, X: KQ = { cua: 'xiu' };
  const mau = (m: KQ[]) => Array.from({ length: soVan }, (_, i) => m[i % m.length] as KQ);
  return (
    <div className="fr-cau">
      <h3>Cầu cho các ván tới</h3>
      <p className="muted fr-note">Các ván tới ra đúng theo thứ tự này, mỗi ván một kết quả, sau lệnh "ván này" nếu có.</p>
      {man === 'tai_xiu' ? (
        <div className="stack fr-gap">
          <div className="row">
            <Button size="sm" kind="tertiary" onClick={() => them([T])}>+ Tài</Button>
            <Button size="sm" kind="tertiary" onClick={() => them([X])}>+ Xỉu</Button>
            <NumberInput id="fr-so-van" label="Số ván mỗi mẫu" size="sm" min={1} max={100} value={soVan}
              onChange={(_e, v) => setSoVan(Math.min(100, Math.max(1, Number(v.value) || 1)))} />
          </div>
          <div className="row">
            <Button size="sm" kind="ghost" onClick={() => them(mau([T]))}>Bệt Tài</Button>
            <Button size="sm" kind="ghost" onClick={() => them(mau([X]))}>Bệt Xỉu</Button>
            <Button size="sm" kind="ghost" onClick={() => them(mau([T, X]))}>Cầu 1-1</Button>
            <Button size="sm" kind="ghost" onClick={() => them(mau([T, T, X, X]))}>Cầu 2-2</Button>
            <Button size="sm" kind="ghost" onClick={() => them(mau([T, X, X]))}>Cầu 1-2</Button>
            <Button size="sm" kind="ghost" onClick={() => them(mau([T, T, T, X, X, X]))}>Cầu 3-3</Button>
          </div>
        </div>
      ) : null}
      <div className="row">
        <NumberInput id="fr-lap" label="Lặp kết quả đang chọn" size="sm" min={1} max={100} value={lap}
          onChange={(_e, v) => setLap(Math.min(100, Math.max(1, Number(v.value) || 1)))} />
        <Button size="sm" kind="tertiary" disabled={!chon} onClick={() => chon && them(Array.from({ length: lap }, () => chon))}>
          Thêm kết quả đang chọn x{lap}
        </Button>
      </div>
      {cau.length ? (
        <>
          <ol className="fr-seq">
            {cau.map((k, i) => (
              <li key={i}>
                <button type="button" className={'fr-bead fr-bead--' + maNgan(man, k).toLowerCase()} title={'Ván tới thứ ' + (i + 1) + ': ' + moTaKQ(man, k) + '. Bấm để bỏ'}
                  onClick={() => setCau((c) => c.filter((_, j) => j !== i))}>{maNgan(man, k)}</button>
              </li>
            ))}
          </ol>
          <div className="row">
            <Button size="sm" kind="primary" disabled={dangGui} onClick={() => setXn(true)}>Đặt cầu {cau.length} ván</Button>
            <Button size="sm" kind="ghost" onClick={() => setCau([])}>Xóa chuỗi</Button>
          </div>
        </>
      ) : <p className="muted fr-note">Chưa có ván nào trong chuỗi.</p>}
      <Modal open={xn} modalHeading={`Đặt cầu ${cau.length} ván cho ${g.ten}`} primaryButtonText="Gửi sang hệ A" secondaryButtonText="Hủy"
        primaryButtonDisabled={dangGui} onRequestClose={() => setXn(false)}
        onRequestSubmit={() => { gui({ hanh_dong: 'cau', game_id: g.id, ds: cau }); setXn(false); setCau([]); }}>
        <p>{cau.length} ván tới sẽ ra lần lượt: {cau.map((k) => moTaKQ(man, k)).join(', ')}.</p>
        <p className="muted">Nối sau các lệnh đang chờ của game này. Hủy từng ván hoặc hủy tất cả ở bảng lệnh.</p>
      </Modal>
    </div>
  );
}

// Bang cau: ket qua da ra (lich su) + cac van da xep (lenh hang doi dang cho), theo thu tu ra
function CauBang({ man, van, lenh }: { man: Man; van: Van | null; lenh: Lenh[] }) {
  const daRa = (van?.lich_su ?? []).slice(-60).map((x) => ({ phien: x.phien_hien ?? x.phien, k: lichSuKQ(man, x), text: lichSuText(man, x) }));
  const cho: { id: number; k: KQ }[] = [];
  for (const l of lenh.filter((x) => x.trang_thai === 'CHO' && x.pham_vi === 'van' && x.phien == null).sort((a, b) => a.id - b.id)) {
    for (let i = 0; i < l.so_lan - l.da_dung; i++) cho.push({ id: l.id, k: l.ket_qua });
  }
  return (
    <section className="panel">
      <div className="panel__head">
        <h2>Cầu</h2>
        <span className="muted">{daRa.length} ván gần nhất, sau đó {cho.length} ván đã xếp</span>
      </div>
      {daRa.length || cho.length ? (
        <ol className="fr-seq fr-seq--road">
          {daRa.map((x, i) => (
            <li key={'r' + i}><span className={'fr-bead fr-bead--' + maNgan(man, x.k).toLowerCase()} title={`#${x.phien}: ${x.text}`}>{maNgan(man, x.k)}</span></li>
          ))}
          {cho.length ? <li className="fr-seq__cut" aria-label="Các ván đã xếp" /> : null}
          {cho.map((x, i) => (
            <li key={'c' + i}><span className={'fr-bead is-cho fr-bead--' + maNgan(man, x.k).toLowerCase()} title={`Ván tới thứ ${i + 1}: ${moTaKQ(man, x.k)} (lệnh #${x.id})`}>{maNgan(man, x.k)}</span></li>
          ))}
        </ol>
      ) : <p className="muted">Chưa có ván nào.</p>}
      {man === 'tai_xiu' && daRa.length ? <ThongKeTX daRa={daRa.map((x) => x.k)} /> : null}
    </section>
  );
}

function ThongKeTX({ daRa }: { daRa: KQ[] }) {
  const t = daRa.filter((k) => maNgan('tai_xiu', k) === 'T').length;
  let chuoi = 0;
  const cuoi = daRa.length ? maNgan('tai_xiu', daRa[daRa.length - 1]) : '';
  for (let i = daRa.length - 1; i >= 0 && maNgan('tai_xiu', daRa[i]) === cuoi; i--) chuoi++;
  return <p className="muted fr-note">Tài {t}, Xỉu {daRa.length - t} trong {daRa.length} ván. Đang bệt {cuoi === 'T' ? 'Tài' : 'Xỉu'} {chuoi} ván.</p>;
}

function lichSuKQ(man: Man, x: LichSu): KQ {
  if (man === 'tai_xiu') return { cua: x.cua === 'tai' ? 'tai' : 'xiu' };
  if (man === 'xoc_dia' && typeof x.d1 === 'number') return { so_do: 4 - x.d1 };
  if (man === 'bau_cua' && typeof x.mo_ta === 'string') return { xuc_xac: x.mo_ta.split(',').map((t) => ['Bầu', 'Cua', 'Tôm', 'Cá', 'Gà', 'Nai'].indexOf(t.trim())) };
  if (man === 'aviator' && typeof x.diem_no === 'number') return { diem_no: x.diem_no };
  if (man === 'lode' && typeof x.de === 'string') return { de: x.de };
  if (man === 'keno' && Array.isArray(x.so)) return { so: x.so };
  if (man === 'sicbo' && typeof x.mo_ta === 'string') {
    const d = x.mo_ta.split(':')[0]?.split(',').map(Number) ?? [];
    if (d.length === 3) return { xuc_xac: d };
  }
  if (Array.isArray(x.cua_thang)) return { cua_thang: x.cua_thang };
  return {};
}
function lichSuText(man: Man, x: LichSu): string {
  if (man === 'tai_xiu' && Array.isArray(x.xuc_xac)) return moTaKQ(man, { xuc_xac: x.xuc_xac });
  if (man === 'keno' && Array.isArray(x.so)) return `tổng ${x.tong}, ${x.loai_thang ?? ''}`;
  if (man === 'lode') return 'Đặc biệt ' + (x.special ?? '');
  return moTaKQ(man, lichSuKQ(man, x));
}

// ================================================================ game theo nguoi choi

function ManNguoi({ d, gui, dangGui }: { d: ManData; gui: (b: Gui) => void; dangGui: boolean }) {
  const g = d.game;
  const [uid, setUid] = useState('');
  const [hanh, setHanh] = useState<'thua' | 'thang' | 'no_hu'>('thua');
  const [heSo, setHeSo] = useState('');
  const [soLan, setSoLan] = useState(1);
  const [xn, setXn] = useState(false);
  const nguoi = d.nguoi_choi ?? [];
  const chonNguoi = nguoi.find((x) => String(x.user_id) === uid);
  const kq: KQ = hanh === 'thang' ? (heSo !== '' ? { thang: true, he_so: Number(heSo) } : { thang: true }) : { [hanh]: true };
  const loi = !/^\d+$/.test(uid) ? 'Nhập mã người chơi' : hanh === 'thang' && heSo !== '' && !(Number(heSo) >= 0 && Number(heSo) <= 10000) ? 'Hệ số từ 0 đến 10000' : null;
  const choCuaNguoi = d.lenh.filter((l) => l.trang_thai === 'CHO' && l.user_id != null);

  return (
    <>
      <div className="fr-cols">
        <section className="panel">
          <div className="panel__head">
            <h2>Người chơi gần đây</h2>
            <span className="muted">60 phút qua, {nguoi.length} người</span>
          </div>
          <div className="fr-scroll fr-scroll--tall">
            {nguoi.length ? (
              <table className="plain fr-table">
                <thead><tr><th>Người chơi</th><th /><th className="fr-r">Lượt</th><th className="fr-r">Cược</th><th className="fr-r">Trả</th><th className="fr-r">Nhà cái</th><th className="fr-r">Lần cuối</th><th /></tr></thead>
                <tbody>
                  {nguoi.map((x) => (
                    <tr key={x.user_id} className={String(x.user_id) === uid ? 'is-chon' : ''}>
                      <td><NguoiChoiLink id={x.user_id} nick={x.nick} /></td>
                      <td>{x.online ? <Tag size="sm" type="green">online</Tag> : null}</td>
                      <td className="fr-r num">{x.so_luot}{x.so_luot_xu ? <span className="muted"> ({x.so_luot_xu} xu)</span> : null}</td>
                      <td className="fr-r num">{vnd(x.cuoc)}</td>
                      <td className="fr-r num">{vnd(x.tra)}</td>
                      <td className="fr-r"><Lai v={x.cuoc - x.tra} /></td>
                      <td className="fr-r muted num">{formatVnDateTime(x.cuoi).slice(11)}</td>
                      <td className="fr-r"><Button size="sm" kind="ghost" onClick={() => setUid(String(x.user_id))}>Chọn</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="muted fr-empty">Chưa ai chơi game này trong 60 phút qua. Vẫn đặt được theo mã người chơi.</p>}
          </div>
        </section>
        <section className="panel fr-chot">
          <div className="panel__head"><h2>Đặt các lượt tới</h2></div>
          <div className="stack fr-gap">
            <TextInput id="fr-uid" labelText="Mã người chơi" value={uid} onChange={(e) => setUid(e.target.value.replace(/\D/g, ''))}
              helperText={chonNguoi ? (chonNguoi.nick ?? '') + (chonNguoi.online ? ', đang online' : '') : undefined} />
            <div className="fr-big3">
              <button type="button" className={'fr-chip fr-chip--big' + (hanh === 'thua' ? ' is-on' : '')} onClick={() => setHanh('thua')}>THUA</button>
              <button type="button" className={'fr-chip fr-chip--big' + (hanh === 'thang' ? ' is-on' : '')} onClick={() => setHanh('thang')}>THẮNG</button>
              {g.hu ? <button type="button" className={'fr-chip fr-chip--big' + (hanh === 'no_hu' ? ' is-on' : '')} onClick={() => setHanh('no_hu')}>NỔ HŨ</button> : null}
            </div>
            {hanh === 'thang' ? (
              <TextInput id="fr-he-so" labelText="Thắng tối thiểu bao nhiêu lần tiền cược (bỏ trống = chỉ cần thắng)" value={heSo}
                onChange={(e) => setHeSo(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="5" />
            ) : null}
            <NumberInput id="fr-so-luot" label="Số lượt tới của người này (1 đến 1000)" min={1} max={1000} value={soLan}
              onChange={(_e, v) => setSoLan(Math.min(1000, Math.max(1, Number(v.value) || 1)))} />
            {loi && uid ? <p className="fr-readout">{loi}</p> : null}
            <Button kind="primary" disabled={!!loi || dangGui} onClick={() => setXn(true)}>Đặt {soLan} lượt: {moTaKQ('nguoi', kq)}</Button>
            {choCuaNguoi.length ? <p className="muted fr-note">{choCuaNguoi.length} lệnh người chơi đang chờ ở bảng lệnh bên dưới.</p> : null}
          </div>
        </section>
      </div>
      <section className="panel">
        <div className="panel__head"><h2>Lượt gần nhất</h2><span className="muted">50 lượt mới nhất, mọi người chơi</span></div>
        <div className="fr-scroll fr-scroll--tall">
          {(d.luot ?? []).length ? (
            <table className="plain fr-table">
              <thead><tr><th>Lúc</th><th>Người chơi</th><th className="fr-r">Cược</th><th className="fr-r">Trả</th></tr></thead>
              <tbody>
                {(d.luot ?? []).map((l) => (
                  <tr key={l.id}>
                    <td className="muted num">{formatVnDateTime(l.luc).slice(11)}</td>
                    <td><NguoiChoiLink id={l.user_id} nick={l.nick} /></td>
                    <td className="fr-r num">{vnd(l.cuoc)}{l.xu ? <span className="muted"> xu</span> : null}</td>
                    <td className={'fr-r num' + (l.tra > l.cuoc && !l.xu ? ' num--neg' : '')}>{vnd(l.tra)}{l.xu ? <span className="muted"> xu</span> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="muted fr-empty">Chưa có lượt nào.</p>}
        </div>
      </section>
      <Modal open={xn} modalHeading={`Đặt kết quả ${g.ten}`} primaryButtonText="Gửi sang hệ A" secondaryButtonText="Hủy"
        primaryButtonDisabled={dangGui} onRequestClose={() => setXn(false)}
        onRequestSubmit={() => { gui({ hanh_dong: 'tao', game_id: g.id, ket_qua: kq, so_lan: soLan, user_id: Number(uid) }); setXn(false); }}>
        <p>{soLan} lượt tới của người chơi #{uid}{chonNguoi?.nick ? ` (${chonNguoi.nick})` : ''} sẽ: <strong>{moTaKQ('nguoi', kq)}</strong>.</p>
        <p className="muted">Lệnh này quyết định tiền người chơi nhận.</p>
      </Modal>
    </>
  );
}

// ================================================================ lenh

function LenhPanel({ g, lenh, gui, dangGui }: { g: Game; lenh: Lenh[]; gui: (b: Gui) => void; dangGui: boolean }) {
  const [huy, setHuy] = useState<Lenh | null>(null);
  const [huyHet, setHuyHet] = useState(false);
  const [loc, setLoc] = useState<'cho' | 'tat_ca'>('cho');
  const soCho = lenh.filter((l) => l.trang_thai === 'CHO').length;
  const ds = loc === 'cho' ? lenh.filter((l) => l.trang_thai === 'CHO') : lenh;
  return (
    <section className="panel">
      <div className="panel__head">
        <h2>Lệnh của game</h2>
        <div className="row">
          <Select id="fr-loc" labelText="" hideLabel size="sm" inline value={loc} onChange={(e) => setLoc(e.target.value as typeof loc)}>
            <SelectItem value="cho" text={`Đang chờ (${soCho})`} />
            <SelectItem value="tat_ca" text={`Tất cả (${lenh.length})`} />
          </Select>
          <Button size="sm" kind="danger--ghost" renderIcon={TrashCan} disabled={!soCho || dangGui} onClick={() => setHuyHet(true)}>Hủy tất cả lệnh chờ</Button>
        </div>
      </div>
      {ds.length ? (
        <div className="fr-scroll fr-scroll--tall">
          <table className="plain fr-table">
            <thead><tr><th>#</th><th>Lúc đặt</th><th>Áp cho</th><th>Kết quả đặt</th><th>Ra thực tế</th><th className="fr-r">Đã dùng</th><th>Trạng thái</th><th /></tr></thead>
            <tbody>
              {ds.map((l) => (
                <tr key={l.id}>
                  <td className="num muted">{l.id}</td>
                  <td><When v={l.luc} /></td>
                  <td>{l.user_id != null ? <NguoiChoiLink id={l.user_id} nick={l.nick} /> : l.phien != null ? <span>Phiên <span className="num">#{l.phien}</span></span> : <span className="muted">Hàng đợi</span>}</td>
                  <td>{moTaKQ(g.man, l.ket_qua)}</td>
                  <td className="muted">{l.ket_qua_ra != null ? raThucTe(g.man, l.ket_qua_ra) : l.loi ?? '-'}</td>
                  <td className="fr-r num">{l.da_dung}/{l.so_lan}</td>
                  <td><Tag size="sm" type={TRANG_THAI_TAG[l.trang_thai] ?? 'gray'}>{TRANG_THAI[l.trang_thai] ?? l.trang_thai}</Tag></td>
                  <td className="fr-r">{l.trang_thai === 'CHO' ? <Button kind="danger--ghost" size="sm" onClick={() => setHuy(l)}>Hủy</Button> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="muted">{loc === 'cho' ? 'Không có lệnh nào đang chờ.' : 'Chưa có lệnh nào.'}</p>}
      <Modal open={!!huy} modalHeading={huy ? `Hủy lệnh #${huy.id}` : ''} primaryButtonText="Hủy lệnh" secondaryButtonText="Đóng" danger
        primaryButtonDisabled={dangGui} onRequestClose={() => setHuy(null)}
        onRequestSubmit={() => { if (huy) gui({ hanh_dong: 'huy', id: huy.id }); setHuy(null); }}>
        {huy ? <p>{moTaKQ(g.man, huy.ket_qua)}, còn {huy.so_lan - huy.da_dung} lượt chưa dùng. Lượt đã dùng không đổi.</p> : null}
      </Modal>
      <Modal open={huyHet} modalHeading={`Hủy tất cả lệnh chờ của ${g.ten}`} primaryButtonText="Hủy tất cả" secondaryButtonText="Đóng" danger
        primaryButtonDisabled={dangGui} onRequestClose={() => setHuyHet(false)}
        onRequestSubmit={() => { gui({ hanh_dong: 'huy_het', game_id: g.id }); setHuyHet(false); }}>
        <p>{soCho} lệnh đang chờ (cầu, hàng đợi, lệnh ván này, lệnh người chơi) sẽ bị hủy. Game chạy ngẫu nhiên lại như thường.</p>
      </Modal>
    </section>
  );
}

function raThucTe(man: Man, r: unknown): string {
  if (Array.isArray(r)) {
    if (man === 'tai_xiu' || man === 'bau_cua') return moTaKQ(man, { xuc_xac: r as number[] });
    if (man === 'xoc_dia') return moTaKQ(man, { dong_xu: r as number[] });
    if (man === 'keno') return (r as number[]).join(' ');
  }
  if (typeof r === 'number') return man === 'aviator' ? r.toFixed(2) + 'x' : String(r);
  if (r && typeof r === 'object') {
    const o = r as Record<string, unknown>;
    if (Array.isArray(o.xucXac)) return moTaKQ(man, { xuc_xac: o.xucXac as number[] });
    if (typeof o.special === 'string') return 'Đặc biệt ' + o.special;
    if (typeof o.tongThang === 'number') return 'Thắng ' + vnd(o.tongThang);
    if (typeof o.nhan === 'number') return 'Nhận ' + vnd(o.nhan);
  }
  return JSON.stringify(r).slice(0, 80);
}
